import { DOCUMENT, isPlatformServer } from '@angular/common';
import { DestroyRef, inject, Injectable, PLATFORM_ID } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';

@Injectable()
export class ContentAnchorScrollService {
  // stabilize layout before scrolling.
  private readonly anchorScrollDelayMs: number = 250;

  private readonly router: Router = inject(Router);
  private readonly document: Document = inject(DOCUMENT);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);
  private readonly isServer: boolean = isPlatformServer(inject(PLATFORM_ID));

  private currentFragment: string | undefined;
  private pendingAnchorScrollTimeout: number | undefined;

  public constructor() {
    if (this.isServer) {
      return;
    }

    this.destroyRef.onDestroy(() => {
      this.clearPendingAnchorScroll();
    });

    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe((event: NavigationEnd) => {
      this.currentFragment = this.getFragmentFromUrl(event.urlAfterRedirects) ?? this.getFragmentFromUrl(event.url);
      this.scheduleScrollToCurrentAnchor();
    });
  }

  public scheduleScrollToCurrentAnchor(): void {
    if (this.isServer) {
      return;
    }

    this.clearPendingAnchorScroll();

    const fragment: string | undefined = this.getCurrentFragment();

    if (!fragment) {
      return;
    }

    this.pendingAnchorScrollTimeout = this.document.defaultView?.setTimeout(() => {
      this.pendingAnchorScrollTimeout = undefined;
      this.scrollToContentAnchor(fragment);
    }, this.anchorScrollDelayMs);
  }

  private scrollToContentAnchor(fragment: string): void {
    const anchor: HTMLElement | undefined = this.getContentAnchorElement(fragment);

    if (!anchor) {
      return;
    }

    anchor.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  private getContentAnchorElement(fragment: string): HTMLElement | undefined {
    return this.document.getElementById(fragment) ?? undefined;
  }

  private getCurrentFragment(): string | undefined {
    return this.currentFragment ?? this.getFragmentFromUrl(this.router.url);
  }

  private getFragmentFromUrl(url: string): string | undefined {
    return this.router.parseUrl(url).fragment ?? undefined;
  }

  private clearPendingAnchorScroll(): void {
    if (this.pendingAnchorScrollTimeout !== undefined) {
      this.document.defaultView?.clearTimeout(this.pendingAnchorScrollTimeout);
      this.pendingAnchorScrollTimeout = undefined;
    }
  }
}
